/**
 * Local-first persistence of a PDF's annotation overlay, keyed by the file's
 * pdf.js fingerprint.
 *
 * Mirrors `useBlobDocument` with ONE twist: the document page is created
 * LAZILY on the first annotation (merely VIEWING a PDF must not create junk
 * pages). Flow per opened file:
 *   1. fingerprint → lookup (`getPdfAnnotationPageByFingerprintQuery`);
 *      found → the reactive block query seeds the model (annotations
 *      re-attach), missing → empty in-memory model.
 *   2. First edit without a page → `createDocumentPageMutation` writes
 *      page + content block in one tx (same as sheets/decks: title, trash,
 *      version history, sharing and sync all reused, zero new schema).
 *   3. Later edits → debounced single-row upsert via `syncPageBlocksMutation`.
 *   4. Remote rows (another device) refresh the model while the user is idle.
 */
import { createEffect, createSignal, onCleanup, untrack } from "solid-js";
import type { Block } from "@folio/powersync";
import {
  createDocumentPageMutation,
  getPdfAnnotationPageByFingerprintQuery,
  PDF_ANNOTATIONS_BLOCK_TYPE,
  syncPageBlocksMutation,
} from "@folio/shared";
import { usePageBlocksQuery } from "@folio/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import {
  blockToPdfDoc,
  emptyPdfAnnotationDoc,
  type PdfAnnotationDoc,
  pdfAnnotationsPlainText,
  pdfDocToBlockNode,
} from "../lib/pdf-annotations";
import { pickContentBlock } from "../lib/document-blocks";

const AUTOSAVE_DEBOUNCE_MS = 600;
const REMOTE_IDLE_MS = 1500;

interface UsePdfAnnotationsOptions {
  /** pdf.js fingerprint of the open file (null = no file open). */
  fingerprint: () => string | null;
  fileName: () => string;
}

export function usePdfAnnotations(options: UsePdfAnnotationsOptions) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const [pageId, setPageId] = createSignal<string | null>(null);
  const [model, setModel] = createSignal<PdfAnnotationDoc | null>(null);
  const [isSaving, setIsSaving] = createSignal(false);
  const [lastSavedAt, setLastSavedAt] = createSignal<number | null>(null);
  const [saveError, setSaveError] = createSignal(false);

  let lastSyncedContent = "";
  let lastLocalEditAt = 0;
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let saveChain: Promise<void> = Promise.resolve();
  let seededFor: string | null = null;

  // Reactive block rows of the annotation page (empty until a page exists).
  const blocks = usePageBlocksQuery({
    db: () => db(),
    drizzle,
    pageId: () => pageId() ?? "",
  });

  // ── 1. Fingerprint changed (file opened/closed): reset + lookup ──────────
  createEffect(() => {
    const fp = options.fingerprint();
    seededFor = null;
    setPageId(null);
    setModel(null);
    setLastSavedAt(null);
    setSaveError(false);
    lastSyncedContent = "";
    if (!fp) {
      return;
    }
    const dbInstance = drizzle();
    if (!dbInstance) {
      setModel(emptyPdfAnnotationDoc(fp, untrack(options.fileName)));
      seededFor = fp;
      return;
    }
    void (async () => {
      try {
        const rows = await getPdfAnnotationPageByFingerprintQuery(dbInstance, fp);
        if (untrack(options.fingerprint) !== fp) {
          return; // another file was opened meanwhile
        }
        const existing = rows[0]?.page_id ?? null;
        if (existing) {
          setPageId(existing); // the block query effect below seeds the model
        } else {
          setModel(emptyPdfAnnotationDoc(fp, untrack(options.fileName)));
          seededFor = fp;
        }
      } catch (err) {
        console.error("[pdf] annotation lookup failed:", err);
        setModel(emptyPdfAnnotationDoc(fp, untrack(options.fileName)));
        seededFor = fp;
      }
    })();
  });

  // ── 2. Seed / remote-refresh from the block rows ─────────────────────────
  createEffect(() => {
    const rows = blocks.data;
    const id = pageId();
    const fp = untrack(options.fingerprint);
    if (!rows || !id || !fp || blocks.isLoading) {
      return;
    }
    const contentBlock: Block | undefined = pickContentBlock(rows, id);
    const remoteContent = contentBlock?.content ?? "";
    if (seededFor !== fp) {
      // Initial seed: annotations of this fingerprint re-attach.
      seededFor = fp;
      lastSyncedContent = remoteContent;
      setModel(blockToPdfDoc(contentBlock, fp, untrack(options.fileName)));
      return;
    }
    if (remoteContent === "" || remoteContent === lastSyncedContent) {
      return;
    }
    if (Date.now() - lastLocalEditAt < REMOTE_IDLE_MS) {
      return;
    }
    lastSyncedContent = remoteContent;
    setModel(blockToPdfDoc(contentBlock, fp, untrack(options.fileName)));
  });

  // ── 3. Persist (lazy page create on first edit) ──────────────────────────
  const persist = (current: PdfAnnotationDoc) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    const content = JSON.stringify(current);
    if (content === lastSyncedContent) {
      return;
    }
    lastSyncedContent = content;
    setIsSaving(true);
    setSaveError(false);
    saveChain = saveChain
      .then(async () => {
        if (untrack(options.fingerprint) !== current.fingerprint) {
          return undefined; // file changed mid-save: drop the write
        }
        // Read the page id INSIDE the serialized chain: a previous queued
        // save may have just created the page (never create it twice).
        const existingPage = untrack(pageId);
        if (!existingPage) {
          const id = await createDocumentPageMutation(dbInstance, uid, PDF_ANNOTATIONS_BLOCK_TYPE, {
            title: current.fileName,
            content,
            text: pdfAnnotationsPlainText(current),
          });
          setPageId(id);
        } else {
          await syncPageBlocksMutation(dbInstance, existingPage, uid, [
            pdfDocToBlockNode(existingPage, current),
          ]);
        }
        setLastSavedAt(Date.now());
        return undefined;
      })
      .catch((err: unknown) => {
        setSaveError(true);
        console.error(
          "[pdf] Error saving annotations:",
          err instanceof Error ? err.message : String(err),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  /** Apply a user edit: update the model and schedule a debounced save. */
  const update = (next: PdfAnnotationDoc) => {
    lastLocalEditAt = Date.now();
    setModel(next);
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      saveTimeout = null;
      persist(next);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const flush = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    const current = untrack(model);
    if (current) {
      persist(current);
    }
  };

  onCleanup(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
      const current = untrack(model);
      if (current) {
        persist(current);
      }
    }
  });

  return { model, update, flush, pageId, isSaving, lastSavedAt, saveError };
}
